import React, { useCallback, useContext, useEffect } from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { AgGridReact } from 'ag-grid-react';
import FiltersContext from '../../Context/FiltersContext';
import unscopedStyles from './ag-grid.unscoped.css';
import {
    registerScrollHandler,
    unregisterScrollHandler
} from '../../utils/scroll-handler';
import { downloadCSV } from '../../utils/csv';
import { DASH_DEFAULT } from '../../utils/Format/Format';
import {
    registerPrintHandler,
    unregisterPrintHandler
} from '../../utils/print-handler';
import styles from './AgGrid.css';
import ascSortIcon from '../../images/ascending_sort.svg';
import descSortIcon from '../../images/descending_sort.svg';
import unSortIcon from '../../images/unsorted_sort.svg';
import HeaderHTMLRenderer from './HeaderHTMLRenderer';
import { isMobileWidth } from '../../theme/layoutUtilities';
import { calculateRowHeight } from '../../cards/WatchlistTable/utils';

let lastFilteredItems;
// let updateTimeout;
let gridApi;

const AgGrid = (props) => {
    const {
        data,
        stickHeaders = true,
        renderers,
        tableConfigs,
        showCSVExport,
        exportFilename,
        Context = FiltersContext,
        filterField = 'ticker',
        contextField = 'filteredTickers',
        customClassName,
        gridHeight
    } = props;

    const filterOptions = {
        isExternalFilterPresent: () => true,
        doesExternalFilterPass: useCallback(
            (node) => {
                // console.log("lastFilteredItems ", lastFilteredItems && lastFilteredItems.size)
                // Nothing filtered
                if (!lastFilteredItems) {
                    return true;
                }

                const value = node.data[filterField];
                return lastFilteredItems.has(value);
            },
            [lastFilteredItems]
        )
    };

    const filtersContext = Context && useContext(Context);
    const filteredItems = filtersContext && filtersContext[contextField];

    if (filteredItems && lastFilteredItems !== filteredItems) {
        lastFilteredItems = filteredItems;
        if (gridApi) {
            gridApi.onFilterChanged();
        }
    }

    const { headers, rows } = data;

    const removeBadChars = (str) => {
        const res = (str || '')
            .toString()
            .replace(/[^&%.\-/\sa-zA-Z0-9]/g, '')
            .replace(/\t/g, ' ')
            .replace(/\s+/g, ' ')
            .replace(/^\s+/, '')
            .replace(/\s+$/, '');
        return res || DASH_DEFAULT;
    };

    // perhaps use the parent name
    const flattenedHeaders = headers
        .reduce(
            (acc, h) => (h.children ? [...acc, ...h.children] : [...acc, h]),
            []
        )
        .filter((h) => h.field || h.csvField || h.csvRenderer)
        .map((h) => ({ ...h, headerName: removeBadChars(h.headerName) }));

    const headerNameToType = flattenedHeaders.reduce(
        (acc, cur) => ({ ...acc, [cur.headerName]: cur.type }),
        {}
    );

    const fieldToHeaderName = flattenedHeaders.reduce(
        (acc, cur) => ({ ...acc, [cur.field]: cur.headerName }),
        {}
    );

    const filteredRows = filteredItems
        ? rows.filter((r) => filteredItems.has(r.ticker))
        : rows;

    const csvData = filteredRows.map((r) =>
        flattenedHeaders.reduce(
            (acc, h) => {
                const renderer = h.csvRenderer && renderers[h.csvRenderer];

                return h.headerName
                    ? {
                          ...acc,
                          [h.headerName]: renderer
                              ? renderer(r)
                              : removeBadChars(r[h.field])
                      }
                    : acc;
            },
            { Ticker: r.ticker }
        )
    );

    useEffect(() => {
        if (!stickHeaders) {
            return () => {};
        }

        const toggleStickyHeader = () => {
            // using vanilla js to have sticky table header
            const tableContainer = document.getElementById('ag-grid-table');
            const headerElement = document.getElementsByClassName(
                'ag-header'
            )[0];
            const tableBottomPosition =
                tableContainer.offsetTop + tableContainer.offsetHeight;
            const tableTopPosition = tableContainer.offsetTop;
            const headerHeight = headerElement.offsetHeight;

            if (
                window.scrollY >= tableBottomPosition - headerHeight ||
                window.scrollY <= tableTopPosition
            ) {
                headerElement.classList.remove('sticky-header');
            } else if (
                window.scrollY < tableBottomPosition - headerHeight * 2 &&
                window.scrollY > tableTopPosition
            ) {
                headerElement.classList.add('sticky-header');
                headerElement.style.width = `${tableContainer.offsetWidth}px`;
            }
        };

        const beforePrint = () => {
            const tableContainer = document.querySelector('.ag-box-container');
            tableContainer.style.width = '';
            tableContainer.style.height = '';
            gridApi.setDomLayout('print');
        };

        const afterPrint = () => {
            const tableContainer = document.querySelector('.ag-box-container');
            tableContainer.style.width = '100%';
            tableContainer.style.height = '100%';
            gridApi.setDomLayout(null);
        };
        if (tableConfigs !== 'ntl-tbl') {
            registerScrollHandler(toggleStickyHeader);
        }

        registerPrintHandler(beforePrint, afterPrint);

        return () => {
            // clean up
            unregisterScrollHandler(toggleStickyHeader);
            unregisterPrintHandler(beforePrint, afterPrint);
        };
    });

    const onGridReady = (params) => {
        gridApi = params.api;
        params.api.sizeColumnsToFit();
        params.columnApi.autoSizeColumns();
    };

    const exportToCSV = () => {
        const sortModel = gridApi ? gridApi.getSortModel() : [];
        const sortedData = csvData.sort((a, b) => {
            for (let i = 0; i < sortModel.length; i++) {
                const { colId, sort } = sortModel[i];
                const res = sort === 'asc' ? 1 : -1;

                const [compareA, compareB] = (() => {
                    const numeric =
                        headerNameToType[fieldToHeaderName[colId]] ===
                        'numericColumn';

                    const A = a[fieldToHeaderName[colId]];
                    const B = b[fieldToHeaderName[colId]];

                    return [
                        (numeric && parseFloat(A === DASH_DEFAULT ? 0 : A)) ||
                            A,
                        (numeric && parseFloat(B === DASH_DEFAULT ? 0 : B)) || B
                    ];
                })();

                if (compareA < compareB) {
                    return -res;
                }

                if (compareA > compareB) {
                    return res;
                }
            }

            return 0;
        });

        downloadCSV(sortedData, exportFilename);
    };

    const getRowHeight = ({ data: rowData } = {}) =>
        calculateRowHeight(
            isMobileWidth(),
            rowData.name,
            rowData.ticker,
            rowData.iClass,
            rowData.openToNewInvestors
        );

    return (
        <div
            className="data-table"
            style={{
                width: '100%',
                height:
                    data && data.rows && data.rows.length > 0
                        ? gridHeight || '100%'
                        : '350px'
            }}
        >
            {showCSVExport && (
                <div className="export-wrapper">
                    <button onClick={exportToCSV} type="button">
                        Export to CSV
                    </button>
                </div>
            )}
            <div
                className={cn(
                    'ag-theme-material',
                    customClassName,
                    'ag-box-container'
                )}
                style={{
                    width: '100%',
                    height: '100%'
                }}
                id="ag-grid-table"
                // role="grid"
                // aria-label="..."
            >
                <AgGridReact
                    getRowHeight={getRowHeight}
                    columnDefs={data && data.headers}
                    rowData={data && data.rows}
                    rowClassRules={{
                        'select-fund': 'data.isSelectFund'
                    }}
                    onGridReady={onGridReady}
                    frameworkComponents={{
                        noRowRenderer: () => (
                            <div className="ag-overlay-loading-center">
                                No funds to display.
                            </div>
                        ),
                        ...renderers
                    }}
                    defaultColDef={{
                        headerComponentParams: {
                            template: `<div class="ag-cell-label-container" role="presentation" >
                                        <div ref="eLabel" class="ag-header-cell-label" role="presentation">
                                        <div class="text" role="presentation">
                                            <span ref="eText" class="ag-header-cell-text" role="presentation"></span>
                                        </div>
                                        <div class="icons" role="presentation">
                                            <span ref="eSortAsc" class="ag-header-icon ag-sort-ascending-icon" ></span>
                                            <span ref="eSortDesc" class="ag-header-icon ag-sort-descending-icon" ></span>
                                            <span ref="eSortNone" class="ag-header-icon ag-sort-none-icon" ></span>
                                        </div>
                                        </div>
                                    </div>`
                        },
                        headerComponent: HeaderHTMLRenderer,
                        unSortIcon: true,
                        sortable: true,
                        icons: {
                            sortAscending: `<img src="${ascSortIcon}" alt="sort in ascending order arrow" />`,
                            sortDescending: `<img src="${descSortIcon}" alt="sort in descending order arrow" />`,
                            sortUnSort: `<img src="${unSortIcon}" alt="unsorted arrows" />`
                        }
                    }}
                    groupHeaderHeight="40"
                    noRowsOverlayComponent="noRowRenderer"
                    suppressMovableColumns
                    suppressCellSelection
                    suppressRowClickSelection
                    suppressClickEdit
                    suppressColumnVirtualisation
                    suppressPaginationPanel
                    {...tableConfigs.props}
                    {...filterOptions}
                    autoHeight
                />
            </div>
            <Helmet>
                <style>{unscopedStyles}</style>
            </Helmet>
            <style jsx>{styles}</style>
        </div>
    );
};

AgGrid.propTypes = {
    data: PropTypes.shape({
        rows: PropTypes.instanceOf(Array),
        headers: PropTypes.arrayOf(PropTypes.string)
    }),
    renderers: PropTypes.objectOf(PropTypes.func),
    tableConfigs: PropTypes.objectOf(
        PropTypes.oneOf[(PropTypes.string, PropTypes.number)]
    )
};

AgGrid.defaultProps = {
    data: {},
    renderers: {},
    tableConfigs: {}
};

export default AgGrid;
